import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Link,
	TextField,
	Typography,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { FC, FormEvent, ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { installShopifyStoreRequest } from 'redux/reducers/shopifyApps/reducer';
import { getInstallShopifyStoreLoader } from 'redux/reducers/shopifyApps/selectors';
import { TShopifyStore } from 'redux/reducers/shopifyApps/types';
import { COLORS } from 'services/constants/colors';

const STORE_DOMAIN_HELP_URL = 'https://help.easproject.com/hc/en-gb/articles/6485082631711';

// Shopify Admin Store Domain, e.g. `your-store.myshopify.com`
const SHOP_DOMAIN_REGEX = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

// Accepts the domain pasted together with the protocol or the admin path.
const normalizeShopDomain = (value: string) =>
	value
		.trim()
		.toLowerCase()
		.replace(/^https?:\/\//, '')
		.replace(/\/.*$/, '');

type TConnectStoreDialogProps = {
	open: boolean;
	appId: number;
	/** Store to reinstall the app in: the dialog starts right from the installation steps */
	store?: TShopifyStore | null;
	onClose: () => void;
	onSuccess: () => void;
};

const Step: FC<{ number: number; children: ReactNode }> = ({ number, children }) => (
	<Box sx={{ display: 'flex', gap: 1 }}>
		<Typography sx={{ color: COLORS.PRIMARY, fontWeight: 'bold', minWidth: '16px' }}>
			{number}.
		</Typography>
		<Typography component="div">{children}</Typography>
	</Box>
);

export const ConnectStoreDialog: FC<TConnectStoreDialogProps> = ({
	open,
	appId,
	store = null,
	onClose,
	onSuccess,
}) => {
	const dispatch = useDispatch();
	const loading = useSelector(getInstallShopifyStoreLoader);
	const [shopDomain, setShopDomain] = useState('');
	const [submitted, setSubmitted] = useState(false);
	// The link to the app installation in Shopify admin, received after the store is connected
	const [installUrl, setInstallUrl] = useState<string | null>(null);
	const [connected, setConnected] = useState(false);

	useEffect(() => {
		if (!open) return;
		setShopDomain(store?.shopDomain ?? '');
		setSubmitted(false);
		setInstallUrl(store?.installUrl ?? null);
		setConnected(!!store);
	}, [open, store]);

	const normalizedShopDomain = normalizeShopDomain(shopDomain);
	const shopDomainError = submitted && !SHOP_DOMAIN_REGEX.test(normalizedShopDomain);

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();
		setSubmitted(true);
		if (!SHOP_DOMAIN_REGEX.test(normalizedShopDomain)) return;

		dispatch(
			installShopifyStoreRequest({
				appId,
				shopDomain: normalizedShopDomain,
				onSuccess: (response) => {
					setConnected(true);
					setInstallUrl(response?.installUrl ?? null);
					onSuccess();
				},
			}),
		);
	};

	const handleInstall = () => {
		if (!installUrl) return;
		window.open(installUrl, '_blank', 'noopener,noreferrer');
		onClose();
	};

	return (
		<Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ color: COLORS.PRIMARY, fontWeight: 'bold' }}>
				{store ? 'Reinstall App' : 'Connect Dev App to new Store'}
			</DialogTitle>
			<form onSubmit={handleSubmit} noValidate autoComplete="off">
				<DialogContent>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						{/* On reinstall the store is already known: only the installation steps are shown */}
						{!store && (
							<>
								<Step number={1}>
									Identify your Shopify Admin Store Domain following{' '}
									<Link href={STORE_DOMAIN_HELP_URL} target="_blank" rel="noopener noreferrer">
										this guide
									</Link>
									.
								</Step>
								<Step number={2}>Enter the Domain address into the domain input field.</Step>
								<TextField
									label="Shopify Admin Store Domain"
									placeholder="your-store.myshopify.com"
									value={shopDomain}
									onChange={(event) => setShopDomain(event.target.value)}
									required
									autoFocus
									disabled={connected || loading}
									error={shopDomainError}
									helperText={
										shopDomainError ? 'Enter the domain in the format your-store.myshopify.com' : ''
									}
									fullWidth
								/>
							</>
						)}
						{connected && (
							<>
								<Step number={store ? 1 : 3}>
									<b>Review Permissions:</b> A new window will open redirecting you to your Shopify
									admin. Review the app name and the data access permissions it requests.
								</Step>
								<Step number={store ? 2 : 4}>
									<b>Confirm Installation:</b> Click <b>Install</b> (or <b>Install app</b>) button
									to finalise the installation.
								</Step>
								{!installUrl && (
									<Typography color="error">The installation link was not received.</Typography>
								)}
							</>
						)}
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
					<Button variant="outlined" onClick={onClose} disabled={loading}>
						{connected ? 'Close' : 'Cancel'}
					</Button>
					{connected ? (
						<Button
							variant="contained"
							startIcon={<OpenInNew />}
							disabled={!installUrl}
							onClick={handleInstall}
						>
							Install App
						</Button>
					) : (
						<Button variant="contained" type="submit" disabled={loading}>
							{loading ? <CircularProgress size={20} /> : 'Connect'}
						</Button>
					)}
				</DialogActions>
			</form>
		</Dialog>
	);
};
